"use client";

import {
  Box,
  Button,
  Dialog,
  Flex,
  Icon,
  Portal,
  Text,
  useToken,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { TbForbidFilled } from "react-icons/tb";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import { WISTIA_MEDIA_ID } from "@/_components/ui/GroupCandidates/constant";
import { ScrollReveal } from "./ScrollReveal";
import { DashboardMock } from "./DashboardMock";

const pulseDot = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 var(--pulse-color); }
  50% { box-shadow: 0 0 0 6px transparent; }
`;

export function LandingHero() {
  const tHero = useTranslations("landing.redesign.hero");
  const tBadge = useTranslations("landing.hero");
  const router = useRouter();
  const [pulseBase] = useToken("colors", ["success.500"]);
  const [openVideo, setOpenVideo] = useState(false);

  return (
    <Box
      as="section"
      id="hero-section"
      aria-labelledby="landing-hero-heading"
      minH={{ base: "auto", md: "calc(100dvh - 64px)" }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      py={{ base: 10, md: 0 }}
      px={{ base: 4, md: 10 }}
      position="relative"
      overflow="hidden"
      bg="neutral.50"
    >
      <Box
        position="absolute"
        w="500px"
        h="500px"
        borderRadius="full"
        bg="primary.50"
        filter="blur(80px)"
        opacity={0.5}
        pointerEvents="none"
        top="-100px"
        right="-100px"
      />
      <Box
        position="absolute"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="success.100"
        filter="blur(80px)"
        opacity={0.5}
        pointerEvents="none"
        bottom={0}
        left="-80px"
      />

      <Flex
        maxW="8xl"
        w="full"
        mx="auto"
        align="center"
        gap={{ base: 10, lg: 15 }}
        flexDir={{ base: "column", lg: "row" }}
        position="relative"
        zIndex={1}
        py={{ base: 4, md: 8 }}
      >
        <Box flex={1} position="relative" zIndex={2}>
          <ScrollReveal>
            <Flex
              as="span"
              display="inline-flex"
              alignItems="center"
              gap={2}
              bg="white"
              border="1px solid"
              borderColor="blackAlpha.100"
              borderRadius="full"
              py={1.5}
              pl={3}
              pr={3.5}
              fontSize="12px"
              color="neutral.700"
              mb={7}
              boxShadow="0 2px 8px rgba(0,0,0,0.06)"
            >
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg="success.500"
                animation={`${pulseDot} 2s infinite`}
                css={
                  {
                    "--pulse-color": `color-mix(in srgb, ${pulseBase} 40%, transparent)`,
                  } as React.CSSProperties
                }
              />
              {tBadge("title")}
            </Flex>

            <Text
              id="landing-hero-heading"
              fontWeight="semibold"
              lineHeight={1.08}
              letterSpacing="-1.5px"
              color="neutral.400"
              mb={6}
              fontFamily="heading"
              fontSize={{ base: "3xl", sm: "4xl", md: "6xl" }}
            >
              {tHero("line1")}
              <br />
              <Text
                as="span"
                display="block"
                color="primary.600"
                fontFamily="heading"
                fontWeight="bold"
                fontSize={{ base: "3xl", sm: "4xl", md: "6xl" }}
              >
                {tHero("line_accent")}
              </Text>
              <Text
                as="span"
                display="block"
                color="primary.600"
                fontWeight="bold"
                fontFamily="heading"
                fontSize={{ base: "3xl", sm: "4xl", md: "6xl" }}
              >
                {tHero("line_outline")}
              </Text>
            </Text>

            <Text
              fontSize="md"
              color="neutral.500"
              lineHeight={1.7}
              maxW="400px"
              mb={9}
              fontWeight="300"
            >
              {tHero("sub")}
            </Text>

            <Flex
              gap={3}
              align={{ base: "stretch", sm: "center" }}
              flexDir={{ base: "column", sm: "row" }}
              w={{ base: "full", sm: "auto" }}
            >
              <Button
                display="inline-flex"
                alignItems="center"
                h={12}
                px={8}
                gap={2}
                py={3.5}
                bg="primary.600"
                color="white"
                borderRadius="full"
                fontSize="14px"
                fontWeight="500"
                w={{ base: "full", sm: "auto" }}
                boxShadow="0 4px 20px rgba(91, 96, 253, 0.35)"
                _hover={{
                  bg: "primary.400",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 28px rgba(91, 96, 253, 0.4)",
                }}
                onClick={() => router.push("/auth/register")}
              >
                {tHero("cta_primary")}
              </Button>
              <Button
                visual="none-style"
                borderRadius="full"
                py={3.5}
                fontSize="14px"
                fontWeight="400"
                color="neutral.900"
                w={{ base: "full", sm: "auto" }}
                _hover={{
                  color: "primary.500",
                  bg: "transparent",
                  borderColor: "none",
                  shadow: "none",
                }}
                onClick={() => setOpenVideo(true)}
              >
                {tHero("cta_secondary")}
              </Button>
            </Flex>

            <Dialog.Root
              open={openVideo}
              onOpenChange={(e) => setOpenVideo(e.open)}
              size="xl"
              placement="center"
            >
              <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner backdropFilter="blur(2px)">
                  <Dialog.Content
                    maxW={{ base: "95vw", md: "72vw", lg: "64vw" }}
                    rounded="3xl"
                    shadow="lg"
                  >
                    <Dialog.Header
                      textStyle="sm"
                      fontWeight="medium"
                      color="neutral.900"
                      bg="neutral.100"
                      roundedTop="3xl"
                      p={4}
                    >
                      <Dialog.Title>{tHero("cta_secondary")}</Dialog.Title>
                      <Dialog.CloseTrigger asChild>
                        <Button
                          visual="none-style"
                          size="md"
                          p={0}
                          _hover={{
                            bg: "transparent",
                            transform: "rotate(90deg)",
                            shadow: "none",
                          }}
                          h={10}
                          transition="all 0.2s ease"
                        >
                          <Icon
                            as={TbForbidFilled}
                            boxSize={6}
                            color="red.500"
                          />
                        </Button>
                      </Dialog.CloseTrigger>
                    </Dialog.Header>
                    <Dialog.Body
                      p={{ base: 2, md: 3 }}
                      pt={0}
                      bg="neutral.100"
                      roundedBottom="3xl"
                    >
                      <Box rounded="2xl" overflow="hidden" bg="white">
                        <WistiaPlayer
                          mediaId={WISTIA_MEDIA_ID.introFull}
                          aspect={16 / 9}
                        />
                      </Box>
                    </Dialog.Body>
                  </Dialog.Content>
                </Dialog.Positioner>
              </Portal>
            </Dialog.Root>

            <Flex
              mt={4}
              fontSize="12px"
              color="neutral.500"
              align="center"
              gap={1.5}
              _before={{
                content: '""',
                w: "16px",
                h: "1px",
                bg: "neutral.500",
              }}
            >
              {tHero("note")}
            </Flex>
          </ScrollReveal>
        </Box>

        <Box
          flex={1}
          w="full"
          maxW={{ base: "full", lg: "50%" }}
          position="relative"
          zIndex={2}
        >
          <ScrollReveal delay={0.2}>
            <DashboardMock />
          </ScrollReveal>
        </Box>
      </Flex>
    </Box>
  );
}
